/**
 * QUIPWA — DrawerReadouts
 *
 * What the «Fichero» skin prints on a deck's drawer front: a status LED and
 * two counters, due and new («012»). The flat skin hides them
 * (DrawerReadouts.css): its chips say the same. Hidden from screen readers in
 * both skins: the chips stay in the page for them (the skin only hides them
 * from view), so a deck reads the same whatever its look.
 */

import type { DeckStats } from '../../types/domain'
import { useI18n } from '../../lib/i18n/i18n-store'
import { lcdCounter, lcdGhost } from '../../lib/ui/lcd'

import './DrawerReadouts.css'

/** Red with overdue cards, amber with cards due today, green when up to date. */
function ledFor(stats: DeckStats | null): 'red' | 'amber' | 'on' {
  if ((stats?.overdue_cards ?? 0) > 0) return 'red'
  if ((stats?.due_today ?? 0) > 0) return 'amber'
  return 'on'
}

export function DrawerReadouts({ stats }: { stats: DeckStats | null }) {
  const { t } = useI18n()
  const due = lcdCounter((stats?.due_today ?? 0) + (stats?.overdue_cards ?? 0))
  const fresh = lcdCounter(stats?.new_cards ?? 0)
  return (
    <span className="drawer-readouts" aria-hidden="true">
      <span className={`led led--${ledFor(stats)}`} />
      <span className="drawer-readout">
        <span className="drawer-readout-label">{t('drawer.due')}</span>
        <span className="lcd" data-ghost={lcdGhost(due)}>{due}</span>
      </span>
      <span className="drawer-readout">
        <span className="drawer-readout-label">{t('drawer.new')}</span>
        <span className="lcd" data-ghost={lcdGhost(fresh)}>{fresh}</span>
      </span>
    </span>
  )
}
